import {useCallback, useState} from "react";
import {useRouter} from "next/router";

// @material-ui/core components
import { makeStyles } from "tss-react/mui";
import { Menu, MenuItem } from "@mui/material";

// core components
import Image from "next/image.js";

// sections for this page
import ForumsModal from "../ForumsModal/ForumsModal.js";
import ForumsReportModal from "../ForumsReportModal/ForumsReportModal.js";
import ForumsTopicEditPost from "../ForumsTopicEditPost/ForumsTopicEditPost.js";
import ForumsModalOptions from "../ForumsModalOptions/ForumsModalOptions.js";

import styles from "../../styles/jss/nextjs-material-kit/pages/forumsStyle.js";

// local assets
import saveIcon from "../../public/img/forums/icons/saveIconBlack.png";
import saveFilledIconBlue from "../../public/img/forums/icons/saveFilledIconBlue.png";
import reportIcon from "../../public/img/forums/icons/reportIconBlack.png";
import shareIcon from "../../public/img/forums/icons/shareIconBlack.png";
import editIcon from "../../public/img/forums/icons/editIconBlack.png";
import deleteIcon from "../../public/img/forums/icons/deleteIcon.png";
import {
    useFavouritesAddRequest,
    useForumsDeleteTopicCommentRequest,
    useForumsDeleteTopicRequest
} from "../../request/api";
import {useTopicListContext} from "../../contexts/topicList";
import {useTopicContext} from "../../contexts/topic";
import {useCommentContext} from "../../contexts/comment";
import {useSavedContext} from "../../contexts/saved";
import { updateQueryString } from "../../util/url.ts";

const useStyles = makeStyles()(styles);

export default function ForumsOptionsMenu(props) {
    const router = useRouter();
    const { classes, cx } = useStyles();
    const { topic, ownPost, onClose, anchorEl, setAnchorEl, setReplyEditorOpen, locked, type, isAuthenticated } = props;

    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState("");
    const [postEditorOpen, setPostEditorOpen] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);

    const commentContext = useCommentContext();
    const topicListContext = useTopicListContext();
    const topicContext = useTopicContext();
    const likedContext = useSavedContext();

    const reloadData = commentContext?.reloadComments ?? topicListContext?.reloadTopics ?? topicContext?.reloadTopic ?? likedContext?.reloadSaves;

    const {
        isMutating: isDeletingTopic,
        trigger: deleteTopic,
        error: addTopicError,
    } = (type === 'topic') ? useForumsDeleteTopicRequest(
      { topicId: topic?.topicId },
      undefined,
      {
          withCredentials: true,
      }
    ) : useForumsDeleteTopicCommentRequest(
      { topicId: topic?.topicId, commentId: topic.commentId },
      undefined,
      {
          withCredentials: true,
      }
    );

    const {
        isMutating: isUpdatingFavourites,
        trigger: addToFavourites,
        error: favouritesError,
    } = useFavouritesAddRequest(
      { type: "forumtopic", entityId: topic?.topicId },
      undefined,
      { withCredentials: true }
    );

    const handleDelete = useCallback(async () => {
        await deleteTopic();
        await reloadData();
        setModalOpen(false);
    }, [onClose]);

    const handleSaveToggle = useCallback(async () => {
        if (isAuthenticated === false) {
            await router.push(updateQueryString(updateQueryString(router.asPath, "login", null), "redirectUrl", router.asPath));
        }
        else {
            await addToFavourites();
            await reloadData();
        }
    }, []);

    const handleSaveOpen = useCallback(async ()  => {
        if (isAuthenticated === false) {
            await router.push(updateQueryString(updateQueryString(router.asPath, "login", null), "redirectUrl", router.asPath));
        }
        else {
            setModalOpen(true);
            setModalType("save");
            onClose();
            setAnchorEl(null);
        }
    }, []);

    const handleShareOpen = () => {
        setModalOpen(true);
        setModalType("share");
        onClose();
        setAnchorEl(null); 
    };

    const handleReportOpen = () => {
        setReportModalOpen(true);
        onClose();
        setAnchorEl(null);
    };


    const handleDeleteOpen = () => {
        setModalOpen(true);
        setModalType("delete");
        onClose();
        setAnchorEl(null);
    };


    const openEditor = () => {
        if (props.initial === true) {
            setPostEditorOpen(true);
        } else {
            setReplyEditorOpen(true);
        }
        onClose();
        setAnchorEl(null);
    }

    const closeEditor = () => {
        setPostEditorOpen(false);
    }

    return (
        <>
            {ownPost ?
                <Menu className={classes.optionsMenu}
                    open={props.open}
                    onClose={props.onClose}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}>
                    <MenuItem className={classes.optionsMenuItem} onClick={openEditor} disabled={locked}>
                        <Image className={classes.icon} src={editIcon} alt={"edit-icon"} width={25} height={25} />
                        <p className={classes.optionsMenuItemText}>Edit</p>
                    </MenuItem>
                    <MenuItem className={classes.optionsMenuItem} onClick={handleDeleteOpen} disabled={locked}>
                        <Image className={classes.icon} src={deleteIcon} alt={"delete-icon"} width={25} height={25} />
                        <p className={classes.optionsMenuItemText}>Delete</p>
                    </MenuItem>
                </Menu>
                :
                <Menu className={classes.optionsMenu}
                    open={props.open}
                    onClose={props.onClose}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}>
                    {type === 'topic' ? <>
                        {topic.isSaved === true ?
                            <MenuItem className={classes.optionsMenuItem} onClick={handleSaveToggle}>
                                <Image className={classes.icon} src={saveFilledIconBlue} alt={"filled-save-icon"} width={25} height={25} />
                                <p className={classes.optionsMenuItemText}>Unsave</p>
                            </MenuItem> :
                            <MenuItem className={classes.optionsMenuItem} onClick={handleSaveOpen}>
                                <Image className={classes.icon} src={saveIcon} alt={"save-icon"} width={25} height={25} />
                                <p className={classes.optionsMenuItemText}>Save</p>
                            </MenuItem>
                        }
                    </> : null}
                    <MenuItem className={classes.optionsMenuItem} onClick={handleShareOpen}>
                        <Image className={classes.icon} src={shareIcon} alt={"share-icon"} width={25} height={25} />
                        <p className={classes.optionsMenuItemText}>Share</p>
                    </MenuItem>

                    {type === 'comment' && isAuthenticated === true ? <>
                        <MenuItem className={classes.optionsMenuItem} onClick={handleReportOpen}>
                            <Image className={classes.icon} src={reportIcon} alt={"report-icon"} width={25} height={25} />
                            <p className={classes.optionsMenuItemText}>Report</p>
                        </MenuItem>
                    </> : null}
                </Menu>
            }
            <ForumsModal open={modalOpen} onClose={() => setModalOpen(false)} closeButton={true} width={classes.width30} >
                <ForumsModalOptions type={modalType} onClose={() => setModalOpen(false)} setSaved={handleSaveToggle} handleDelete={handleDelete} topic={topic} community={props.community} />
            </ForumsModal>
            <ForumsModal open={postEditorOpen} onClose={closeEditor} closeButton={true} width={classes.width50} >
                <ForumsTopicEditPost topic={topic} setEdited={props.setEdited} onClose={closeEditor} />
            </ForumsModal>
            <ForumsModal open={reportModalOpen} onClose={() => setReportModalOpen(false)} closeButton={true} width={classes.width30} >
                <ForumsReportModal onClose={() => setReportModalOpen(false)} reloadData={reloadData} commentId={topic.commentId} />
            </ForumsModal>
        </>
    )
}

